import {
  type AnalyticsEnvironmentConfig,
  type InitializeSegmentOptions,
  createSegmentDestination,
  getAvoEnv,
  initializeSegment,
  insertCookieTrackingScript,
} from '@susu/analytics-client'

import Avo from '../../avo/avo'
import type { ApplicationIdValueType, AvoEnv } from '../../avo/avo'
import { SEGMENT_WRITE_KEY, type Environment } from '../config'
import { initDatadog } from '../datadog'

const APPLICATION_ID = 'PREFERENCE_CENTER' as unknown as ApplicationIdValueType
const APP_VERSION = '1.0.0'

export interface PreferenceCenterAnalyticsConfig {
  oneTrustId?: string
  trackingGaCookie?: string
}

/** Suppresses default Avo `[avo] ...` console output from the generated client. */
const silentAvoLogger = {
  logDebug: () => true,
  logWarn: () => true,
  logError: () => true,
} as const

let avoInitialized = false

const initAvo: NonNullable<
  InitializeSegmentOptions<AvoEnv, ApplicationIdValueType>['initAvo']
> = (options, systemProperties, destinationOptions, segmentDestination) => {
  Avo.initAvo(
    { ...options, webDebugger: false, avoLogger: silentAvoLogger },
    {
      ...systemProperties,
      affiliateMarker: '',
      awc: '',
    },
    destinationOptions,
    segmentDestination,
  )
  avoInitialized = true
}

const getAnalyticsEnvironmentConfig = (
  env: Environment,
  analyticsConfig: PreferenceCenterAnalyticsConfig,
): AnalyticsEnvironmentConfig => ({
  segmentWriteKey: SEGMENT_WRITE_KEY[env],
  trackingGaCookie: analyticsConfig.trackingGaCookie,
  environmentType: env === 'prd' ? 'PRD' : undefined,
  appVersion: APP_VERSION,
})

function safeTrack(fn: () => void) {
  try {
    fn()
  } catch {
    // Analytics failures (e.g. Avo Segment destination missing, ad blockers)
    // must never crash preference updates.
  }
}

/**
 * When Preference Center is embedded (e.g. Customer Hub), the host may already
 * have called `initializeSegment`. That early-returns without wiring this
 * package's Avo client, leaving `Segment` undefined and `preferencesSaved`
 * throwing on `Segment.logEvent`.
 */
function ensureAvoInitialized(envConfig: AnalyticsEnvironmentConfig): void {
  if (avoInitialized) {
    return
  }

  try {
    initAvo(
      { env: getAvoEnv(envConfig.environmentType) as AvoEnv },
      {
        applicationId: APPLICATION_ID,
        version: APP_VERSION,
        clientId: '',
        sessionId: '',
        platform: '',
        lastPlatform: '',
      },
      {},
      createSegmentDestination() as Parameters<typeof initAvo>[3],
    )
  } catch {
    // Non-fatal: tracking stays disabled rather than breaking the UI.
  }
}

export const initializeAnalytics = (
  env: Environment,
  analyticsConfig: PreferenceCenterAnalyticsConfig = {},
): Promise<(() => void) | undefined> => {
  if (analyticsConfig.oneTrustId) {
    insertCookieTrackingScript(analyticsConfig.oneTrustId)
  }

  const envConfig = getAnalyticsEnvironmentConfig(env, analyticsConfig)

  initializeSegment<AvoEnv, ApplicationIdValueType>({
    env: envConfig,
    applicationId: APPLICATION_ID,
    version: APP_VERSION,
    initAvo,
    useWithOneTrust: true,
  })

  ensureAvoInitialized(envConfig)

  return initDatadog(env)
}

export const trackPageLoaded = (language: string) => {
  safeTrack(() => {
    Avo.pageLoaded({
      customDestinationPageName_: 'Preference Center',
      localeVisited: `${language}-${Avo.Country.NL}`,
      productsCart: [],
      category: '',
      subcategory: '',
      currency: Avo.Currency.EUR,
      country: Avo.Country.NL,
      language: language as any,
      locationId: window.location.href,
      userId: '',
      loggedIn: Avo.LoggedIn.FALSE,
      pageType: Avo.PageType.ACCOUNT,
      gaAwClid: null,
      gaClientId: null,
      gaSessionId: null,
      gaSessionNumber: null,
      fbFbp: null,
      fbFbc: null,
    })
  })
}

export const trackPreferencesSaved = (
  language: string,
  newsletter: boolean,
  curatedForYou: boolean,
) => {
  safeTrack(() => {
    Avo.preferencesSaved({
      currency: Avo.Currency.EUR,
      country: Avo.Country.NL,
      language: language as any,
      url: window.location.href,
      locationId: window.location.href,
      userId: '',
      loggedIn: Avo.LoggedIn.FALSE,
      pageType: Avo.PageType.ACCOUNT,
      gaClientId: null,
      gaSessionId: null,
      gaSessionNumber: null,
      fbFbp: null,
      fbFbc: null,
      gaAwClid: null,
      eventLabel: null,
      eventCategory: null,
      eventLocation: 'preference_center',
      communicationPreferences: {
        communicationLanguage: language,
        newsletterStatus: newsletter,
        curatedForYouStatus: curatedForYou,
      },
    })
  })
}
